/**
 * A job's language, as the Patterns page draws and says it (spec "Pattern Mapping" §4.3; Jason, 2026-10-15: "I don't
 * want numerical amounts just plus / minus"): how much a pattern matters is one of five steps, drawn as its circle and
 * said in words, never a number; how many are on the map is read against a working language; a circle's hue is the
 * book's scale.
 */

export const PATTERN_WEIGHTS = [1, 2, 3, 4, 5] as const;
export type PatternWeight = (typeof PATTERN_WEIGHTS)[number];

/** Where a pattern dragged on or added comes in: the middle step. */
export const ADDED_WEIGHT: PatternWeight = 3;

/** A circle's diameter at each step, before it grows to fit its name. */
export const PATTERN_DIAMETERS: Readonly<Record<PatternWeight, number>> = { 1: 76, 2: 90, 3: 104, 4: 120, 5: 134 };

/** What a screen reader hears of how much it matters. */
export function mattersWords(w: PatternWeight): 'matters most' | 'matters' | 'matters a little' {
  return w >= 5 ? 'matters most' : w >= 3 ? 'matters' : 'matters a little';
}

export interface CountZone {
  from: number;
  /** The last count in it; null for the last zone, open-ended. */
  to: number | null;
  words: string;
  /** What would help, in a sentence. */
  hint: string;
  tone: 'bengara' | 'ochre' | 'moss';
}

/**
 * How many patterns on the map make a language NSB and a client can design with (spec §4.3; Jason, 2026-10-17: "it
 * encourages them to find the middle ground where green or success is sort of a central position"). NSB's judgement, not
 * the book's: a house's pool is 163, following the book two steps down from the house patterns reaches 66 to 79, and a
 * language held in mind is a third to a half of that. Changed here when practice says so; the bar never blocks anything.
 */
export const COUNT_ZONES: readonly CountZone[] = [
  { from: 0, to: 11, words: 'Too few to shape a house yet', hint: 'Add the patterns the house can’t do without.', tone: 'bengara' },
  { from: 12, to: 19, words: 'Getting there', hint: 'A few more and it starts to describe the whole house.', tone: 'ochre' },
  { from: 20, to: 40, words: 'A working language', hint: 'Enough to shape the design, few enough to keep in mind.', tone: 'moss' },
  { from: 41, to: 60, words: 'Getting crowded', hint: 'Lower the ones that matter least; the smallest step takes them off.', tone: 'ochre' },
  { from: 61, to: null, words: 'Too many to keep in mind', hint: 'Take off the ones this house can live without.', tone: 'bengara' },
];

export function countZone(count: number): CountZone {
  return COUNT_ZONES.find((z) => count >= z.from && (z.to === null || count <= z.to)) ?? COUNT_ZONES[0]!;
}

/** A pattern's hue, its scale in the book: the house's patterns (and the town's, read and never added) slate. */
export function patternHue(n: number): 'slate' | 'moss' | 'cedar' {
  return n <= 94 ? 'slate' : n <= 204 ? 'moss' : 'cedar';
}

export const HUE_WORDS = 'Colour is the book’s scale: slate the house, moss the building, cedar its construction.';

/** What the book's asterisks on a pattern mean, in a sentence. */
export function asteriskWords(stars: 0 | 1 | 2): string {
  return stars === 2
    ? 'Two asterisks in the book: it is surest of this one.'
    : stars === 1
      ? 'One asterisk in the book: it thinks it is close.'
      : 'No asterisk in the book: it is least sure of this one.';
}
